import React from 'react'

import {without} from 'es-toolkit'

import {PossiblyMixedValue, isMixedValue} from '../utils/form'

import plusIcon from '../../../../commons/src/assets/icons/plus.svg'

import ActionBar from '../../../../commons/src/components/action-bar'
import Chip from '../../../../commons/src/components/chip'
import ContextMenu from '../../../../commons/src/components/context-menu'
import ContextMenuLink from '../../../../commons/src/components/context-menu/ContextMenuLink'
import Icon from '../../../../commons/src/components/icon'
import LinkButton from '../../../../commons/src/components/link-button'
import LoadingIndicator from '../../../../commons/src/components/loading-indicator'
import PopoverHandler from '../../../../commons/src/components/popover-handler'

interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface Props {
  isLoading?: boolean
  disabled?: boolean
  defaultOption?: React.ReactNode
  selectLabel: string
  options: SelectOption[]
  values: PossiblyMixedValue<SelectOption['value'][]>
  onChange: (values: SelectOption['value'][]) => void
  onReset: () => void
}

const MultiSelectFieldPartial = ({
  isLoading = false,
  disabled = false,
  selectLabel,
  defaultOption,
  options,
  values,
  onChange,
  onReset,
}: Props) => {
  const unselectedOptions = options.filter(
    option => !isMixedValue(values) && !values.includes(option.value)
  )

  return (
    <ActionBar>
      {!isLoading ? (
        <>
          {!isMixedValue(values) ? (
            defaultOption
          ) : (
            <Chip emphasized label="Mixed" onClear={onReset} />
          )}
          {!isMixedValue(values) &&
            values &&
            values.map(value => {
              const activeOption = options.find(option => option.value === value)

              return activeOption ? (
                <Chip
                  key={activeOption.value}
                  label={activeOption.label}
                  onClear={() => {
                    onChange(without(values, activeOption.value))
                  }}
                />
              ) : null
            })}
          {!isMixedValue(values) && unselectedOptions.length > 0 && (
            <PopoverHandler
              distanceToAnchor={4}
              popover={({close, anchorRect, ref, style}) => (
                <ContextMenu
                  style={{...style, minWidth: anchorRect?.width}}
                  ref={ref}
                  maxHeight
                  onClose={close}
                >
                  {unselectedOptions.map((option: SelectOption) => (
                    <ContextMenuLink
                      key={option.value}
                      disabled={option.disabled}
                      onClick={() => onChange([...values, option.value])}
                    >
                      {option.label}
                    </ContextMenuLink>
                  ))}
                </ContextMenu>
              )}
            >
              {({toggle, ref}) => (
                <LinkButton
                  ref={ref}
                  onClick={toggle}
                  disabled={disabled}
                  icon={<Icon source={plusIcon} />}
                >
                  {selectLabel}
                </LinkButton>
              )}
            </PopoverHandler>
          )}
        </>
      ) : (
        <LoadingIndicator />
      )}
    </ActionBar>
  )
}

export default MultiSelectFieldPartial
